import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { InfoCircleFilled, UserOutlined } from '@ant-design/icons'
import { Avatar, Button, Col, Divider, List, notification, Row, Select, Space, Tooltip, Typography } from 'antd'
import debounce from 'lodash.debounce'

import { Permission } from '@pollination-solutions/pollination-sdk'

import { AvatarLink, Empty, InputLabel } from 'atoms'
import DeleteButton from 'atoms/DeleteButton'
import SelectPermission from 'atoms/SelectPermission'
import { UserPermission } from 'hooks/useLicenseUsers'
import { OrganizationMemberWithRoles } from 'hooks/useOrganizationMembers'
import { useAuth } from 'auth-context'

interface SearchUsersProps {
  searchUsers: (search?: string) => Promise<OrganizationMemberWithRoles[]>
  selectUsers: React.Dispatch<React.SetStateAction<OrganizationMemberWithRoles[]>>
}

const SearchUsers: React.FunctionComponent<SearchUsersProps> = ({ searchUsers, selectUsers }) => {
  const [search, setSearch] = useState<string>('')
  const { client } = useAuth()
  const [users, setUsers] = useState<OrganizationMemberWithRoles[]>([])

  const debounceSearch = useMemo(() => debounce((search: string) => setSearch(search), 200), [])

  useEffect(() => {
    searchUsers(search).then((users) => setUsers(users))
      .catch(error => {
        notification.error({
          message: error.data.details
        })
      })
  }, [search, searchUsers, setUsers])

  return (
    <Select
      style={{ minWidth: 240 }}
      dropdownMatchSelectWidth
      showSearch
      showArrow
      mode='multiple'
      placeholder='Search Users'
      onSearch={debounceSearch}
      onSelect={(value: string) => {
        const user = users.find(user => user.user.username === value)
        if (user) {
          selectUsers((prev => {
            if (prev.find(u => u.user.username === user.user.username)) {
              return prev
            }
            return [...prev, user]
          }))
        }
      }}
    >
      {
        users.map(user => (
          <Select.Option key={user.user.username} value={user.user.username}>
            <Space size='small' align='center'>
              <Avatar src={user.user.picture} alt='User avatar' size={'small'} />
              <Typography.Text>{user.user.name ?? user.user.username}</Typography.Text>
              <Tooltip title={
                <Space direction='vertical'>
                  <strong>{user.user.username}</strong>
                  <Divider style={{
                    margin: 0
                  }}/>
                  {user.user.description}
                </Space>}
              >
                <InfoCircleFilled 
                style={{
                  color: '#1890ff',
                }} />
              </Tooltip>
            </Space>
          </Select.Option>
        ))
      }
    </Select>
  )
}


export interface LicensePoolUserListProps {
  loading: boolean,
  userPermissions: UserPermission[]
  canAdd: boolean
  permissionOptions: Permission[]
  searchUsers: (search?: string) => Promise<OrganizationMemberWithRoles[]>
  upsertUsersPermission: (users: OrganizationMemberWithRoles[], permission: Permission) => Promise<void>
  removeUserPermission: (user: OrganizationMemberWithRoles) => Promise<void>
}

export const ResourceUserAccessList: React.FunctionComponent<LicensePoolUserListProps> = ({
  loading,
  userPermissions,
  canAdd,
  permissionOptions,
  searchUsers,
  upsertUsersPermission,
  removeUserPermission,
}) => {

  const [usersToAdd, setUsersToAdd] = useState<OrganizationMemberWithRoles[]>([])
  const [permissionToAdd, setPermissionToAdd] = useState<Permission>(permissionOptions[0])
  const [upsertPermissionLoading, setUpsertPermissionLoading] = useState<boolean>(false)

  const upsertPermission = useCallback(() => {
    if (usersToAdd.length === 0) return
    setUpsertPermissionLoading(true)
    upsertUsersPermission(usersToAdd, permissionToAdd)
      .then(() => {
        setUsersToAdd([])
      })
      .catch(error => {
        notification.error({
          message: error.data.details
        })
      })
      .finally(() => {
        setUpsertPermissionLoading(false)
      })
  }, [usersToAdd, permissionToAdd, upsertUsersPermission])

  const removePermission = useCallback((user: OrganizationMemberWithRoles) => {
    return removeUserPermission(user)
      .then(() => {
        setUsersToAdd([])
      })
      .catch(error => {
        notification.error({
          message: error.data.details
        })
      })
  }, [removeUserPermission])

  return (
    <List
      style={{ width: '100%' }}
      loading={loading}
      dataSource={userPermissions}
      locale={{
        emptyText: <Empty
          description='No users have access to this resource.'
        />
      }}
      header={
        <Col span={24}>
          <Space style={{ width: '100%' }} direction='vertical'>
            <Typography.Text>Users who have access.</Typography.Text>
          </Space>
        </Col>
      }
      footer={
        canAdd &&
        <InputLabel label='Add Users' >
          <Space align='center'>
            <Button icon={<UserOutlined />} shape='circle'
              disabled={usersToAdd.length === 0}
              loading={upsertPermissionLoading}
              type='primary'
              onClick={upsertPermission} />
            <Divider type='vertical' />
            {!upsertPermissionLoading && <SearchUsers searchUsers={searchUsers} selectUsers={setUsersToAdd} />}
            {permissionOptions.length > 1 &&
              <SelectPermission
                permissionOptions={permissionOptions}
                value={permissionToAdd}
                onChange={(value) => Promise.resolve(setPermissionToAdd(value))} />
            }
          </Space>
        </InputLabel>
      }
      renderItem={(userPermission, i) =>
        <>
          <List.Item actions={[
            permissionOptions.length > 1
            ? <SelectPermission key={userPermission.user.user.username}
              permissionOptions={[Permission.Read]}
              value={userPermission.permission}
              // TODO: Check this if needed
              onChange={(value) => upsertUsersPermission([userPermission.user], value)} />
            : <></>,
            <DeleteButton key={userPermission.user.user.username}
              title={'Remove Access'} onClick={() => removePermission(userPermission.user)} />,
          ]}>
            <List.Item.Meta
              title={userPermission.user.user.name || userPermission.user.user.username}
              avatar={<AvatarLink src={userPermission.user.user.picture} link={`/${userPermission.user.user.username || userPermission.user.user.name || ''}`} />}
            >
            </List.Item.Meta>
          </List.Item>
        </>
      }
    />
  )
}
